// 세계 법칙 적용 어댑터의 순수 부분(C10b). 던전 입장 때 ruleForRun으로 WorldRuleMods를 한 번 고정하고,
// 아래 헬퍼를 "읽기 호출로만" 곱한다(전투 판정 코드 불변). 반올림 규칙은 헬퍼마다 못 박는다.
import { NEUTRAL_RULE_MODS, WORLD_RULE_REWARD_MULT_MAX, maxRewardMult, type WorldRuleDef, type WorldRuleMods } from '../shared/content/worldRule';

export type RunKind = 'stage' | 'vault' | 'mine' | 'tower' | 'field' | 'raid' | 'party' | 'net' | 'town';
/** 규칙이 적용되는 판: 스테이지·보물고·광산·탑·필드. 레이드·파티·넷·마을은 중립(치트 상한 보호, 휴식과 같음) */
const APPLIES: ReadonlySet<RunKind> = new Set<RunKind>(['stage', 'vault', 'mine', 'tower', 'field']);
export function ruleForRun(def: WorldRuleDef, run: { kind: RunKind }): WorldRuleMods {
  return APPLIES.has(run.kind) ? capRewardMods(def.mods) : NEUTRAL_RULE_MODS;
}
/** 모든 보상 배율(클리어 골드·골드·EXP)을 WORLD_RULE_REWARD_MULT_MAX로 자른다. 상한 이하면 같은 객체를 돌려준다(기기 시계 조작의 이득 제한) */
export function capRewardMods(m: WorldRuleMods): WorldRuleMods {
  if (maxRewardMult(m) <= WORLD_RULE_REWARD_MULT_MAX) return m;
  const c = (v: number): number => Math.min(v, WORLD_RULE_REWARD_MULT_MAX);
  return { ...m, clearGoldMult: c(m.clearGoldMult), goldMult: c(m.goldMult), xpMult: c(m.xpMult) };
}

const pos = (base: number, v: number): number => (base > 0 ? Math.max(1, v) : 0);
/** 스폰 수: 반올림, 기본이 1 이상이면 최소 1 */
export function spawnCount(base: number, m: WorldRuleMods): number { return pos(base, Math.round(base * m.mobCountMult)); }
/** 콤보 유지 시간(ms 또는 tick): 반올림 */
export function comboKeep(base: number, m: WorldRuleMods): number { return Math.round(base * m.comboKeepMult); }
/** 보스 HP: 반올림, 최소 1 */
export function bossHp(base: number, m: WorldRuleMods): number { return pos(base, Math.round(base * m.bossHpMult)); }
/** 보스 크기: 곱 그대로(표시용 실수) */
export function bossScale(base: number, m: WorldRuleMods): number { return base * m.bossScaleMult; }
/** 이동 속도: 곱 그대로 */
export function moveSpeed(base: number, m: WorldRuleMods): number { return base * m.moveSpeedMult; }
/** 받는 피해: 반올림, 맞았으면 최소 1 */
export function damageTaken(base: number, m: WorldRuleMods): number { return pos(base, Math.round(base * m.damageTakenMult)); }
/** 클리어 골드: 클리어 배율 × 골드 배율을 곱한 뒤 내림(보상은 내림, 서버 상한과 같은 방향) */
export function clearGold(base: number, m: WorldRuleMods): number { return Math.floor(base * m.clearGoldMult * m.goldMult + 1e-9); }
/** EXP 보상: 내림 */
export function clearXp(base: number, m: WorldRuleMods): number { return Math.floor(base * m.xpMult + 1e-9); }
/** 별 시간 조건(tick): 더 빡빡한 쪽만 쓴다(규칙이 기본을 느슨하게 만들지 않는다) */
export function starTimeLimit(baseTicks: number, m: WorldRuleMods): number { return m.starTimeTicks === null ? baseTicks : Math.min(baseTicks, m.starTimeTicks); }
/** 별 3개를 땄을 때만 추가 강화석 */
export function star3BonusStones(stars: number, m: WorldRuleMods): number { return stars >= 3 ? m.star3Stones : 0; }
/** 보상 카드 수 */
export function rewardCardCount(base: number, m: WorldRuleMods): number { return base + m.rewardCards; }
/** 보스 클리어 균열 조각: 오늘 이미 받은 수(todayShards)와 하루 상한 안에서만 */
export function riftShardGain(todayShards: number, m: WorldRuleMods): number {
  if (m.riftShardBonus <= 0) return 0;
  return Math.max(0, Math.min(m.riftShardBonus, m.riftShardDayCap - Math.max(0, todayShards)));
}
/** 사망 시 판 종료 여부(true면 부활 없이 기존 사망 처리 경로로 종료) */
export function endsRunOnDeath(m: WorldRuleMods): boolean { return m.deathEndsRun; }
